/**
 * The typed face of `echo-chat-wasm` (web-plan.md F0): every rule both wire
 * ends enforce — markdown, the entity normal form, message splitting, album
 * geometry, the waveform, the avatar hash, contrast math, the emoji
 * vocabulary — is called here and implemented exactly once, in Rust.
 *
 * The raw exports speak JSON strings; this module parses them into the
 * generated dto types so nothing else ever sees a string boundary. Load is
 * explicit and awaited once at startup (`initWasm`), because every caller
 * after that wants synchronous answers — a composer that awaited a promise
 * per keystroke would be the wrong shape.
 */
import initChat, {
  album_height,
  avatar_color,
  avatar_gradient,
  avatar_initials,
  emoji_all,
  emoji_canonical,
  markdown_of,
  markdown_parse,
  normalize_entities,
  split_message,
  unpack_waveform,
} from "../wasm/chat/echo_chat_wasm";

import type { Attachment, Entity } from "../gen/dto";

let ready = false;
let initialization: Promise<void> | null = null;

/** Instantiates the wasm module. Everything else here throws until this has
 * resolved once — the app calls it before the first render. */
export async function initWasm(): Promise<void> {
  if (ready) return;
  initialization ??= initChat()
    .then(() => {
      ready = true;
    })
    .catch((error: unknown) => {
      initialization = null;
      throw error;
    });
  await initialization;
}

export interface ParsedMarkdown {
  text: string;
  entities: Entity[];
}

/** Composer input in, the server's normal form out (chat-api.md §1.2). */
export function parseMarkdown(input: string): ParsedMarkdown {
  return JSON.parse(markdown_parse(input)) as ParsedMarkdown;
}

/** What the composer opens when an edit begins (D20). */
export function toMarkdown(text: string, entities: Entity[]): string {
  return markdown_of(text, JSON.stringify(entities));
}

/** Validate + normalize under `Unknowns::Drop` — run before every send, so a
 * 400 from the server is a bug report and not an ordinary outcome. Throws
 * with the server's own sentence when the input cannot normalize. */
export function normalizeEntities(
  text: string,
  entities: Entity[],
  maxTextUnits: number,
): Entity[] {
  return JSON.parse(normalize_entities(text, JSON.stringify(entities), maxTextUnits)) as Entity[];
}

export interface SplitPart {
  text: string;
  entities: Entity[];
}

export interface SplitResult {
  parts: SplitPart[];
  truncated: boolean;
}

/** D131: an over-long message cut along paragraph → sentence → word → code
 * point, at most ten parts, entities cut with the text. */
export function splitMessage(text: string, entities: Entity[], limit: number): SplitResult {
  return JSON.parse(split_message(text, JSON.stringify(entities), limit)) as SplitResult;
}

/** One height for the whole album frame at `width` (chat-plan.md §6.3). */
export function albumHeight(attachments: Attachment[], width: number): number {
  return album_height(JSON.stringify(attachments), width);
}

/** The 100 five-bit buckets of a voice note's bar chart, from the
 * attachment's base64 `waveform`. */
export function unpackWaveform(waveformBase64: string): Uint8Array {
  const raw = atob(waveformBase64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return unpack_waveform(bytes);
}

/** «Иван Петров» → «ИП» (D73). */
export const avatarInitials = (name: string): string => avatar_initials(name);

/** The person's flat colour — a `var(--accents-…)` token. */
export const avatarColor = (name: string, registeredAtS: number): string =>
  avatar_color(name, BigInt(registeredAtS));

/** The circle's `background-image` gradient (D73). */
export const avatarGradient = (name: string, registeredAtS: number): string =>
  avatar_gradient(name, BigInt(registeredAtS));

/** Canonical spelling of a reaction, or null for a value outside the closed
 * vocabulary — shown as a neutral chip, never sent back (chat-api.md §7). */
export function emojiCanonical(input: string): string | null {
  return emoji_canonical(input) ?? null;
}

let vocabulary: string[] | null = null;

/** The whole closed reaction vocabulary (D152) — what the picker draws. */
export function emojiVocabulary(): string[] {
  vocabulary ??= JSON.parse(emoji_all()) as string[];
  return vocabulary;
}
